import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: "vaccine", label: "Vaccine" },
  { value: "medication", label: "Medication" },
  { value: "grooming", label: "Grooming" },
  { value: "vet_visit", label: "Vet Visit" },
  { value: "custom", label: "Custom" },
];

const RECURRING_OPTIONS = [
  { value: 7, label: "Weekly" },
  { value: 30, label: "Monthly" },
  { value: 90, label: "Every 3 months" },
  { value: 180, label: "Every 6 months" },
  { value: 365, label: "Yearly" },
];

const EMPTY_FORM = {
  pet_id: "", type: "vaccine", title: "", notes: "", due_date: "",
  recurring: false, recurring_interval_days: 365,
};

export default function AddReminderModal({ open, onClose, onSaved, pets, defaultPetId }) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setForm({ ...EMPTY_FORM, pet_id: defaultPetId || (pets[0]?.pet_id || "") });
    }
  }, [open, defaultPetId, pets]);

  if (!open) return null;

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async () => {
    if (!form.pet_id) { setError("Please select a pet"); return; }
    if (!form.title.trim()) { setError("Title is required"); return; }
    if (!form.due_date) { setError("Due date is required"); return; }
    setLoading(true); setError("");
    const body = {
      ...form,
      due_date: new Date(form.due_date).toISOString(),
      recurring_interval_days: form.recurring ? parseInt(form.recurring_interval_days) : null,
    };
    try {
      const res = await fetch(`${API}/reminders`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.detail || "Failed to save reminder");
        return;
      }
      onSaved();
      onClose();
    } catch { setError("Network error"); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md animate-fade-in-up font-['Figtree']">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-800 font-['Nunito']">Add Reminder</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 transition-colors"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-xl">{error}</p>}

          {pets.length > 1 && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Pet</label>
              <select data-testid="reminder-pet-select" value={form.pet_id} onChange={e => set("pet_id", e.target.value)} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40">
                {pets.map(p => <option key={p.pet_id} value={p.pet_id}>{p.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Type</label>
            <div className="grid grid-cols-5 gap-1.5">
              {TYPES.map(t => (
                <button key={t.value} onClick={() => set("type", t.value)}
                  className={`py-2 rounded-xl text-xs font-semibold transition-all ${form.type === t.value ? "bg-[#FF8A65] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  data-testid={`type-${t.value}`}>{t.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Title *</label>
            <input data-testid="reminder-title-input" value={form.title} onChange={e => set("title", e.target.value)} placeholder="e.g. Rabies Vaccine" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Due Date *</label>
            <input data-testid="reminder-date-input" type="date" value={form.due_date} onChange={e => set("due_date", e.target.value)} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Notes (optional)</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Any additional notes..." rows={2} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40 resize-none" />
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="recurring-toggle"
              onClick={() => set("recurring", !form.recurring)}
              className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${form.recurring ? "bg-[#FF8A65]" : "bg-slate-200"}`}
            >
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.recurring ? "translate-x-6" : "translate-x-1"}`} />
            </button>
            <span className="text-sm font-medium text-slate-700">Recurring reminder</span>
          </div>

          {form.recurring && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Repeat every</label>
              <select value={form.recurring_interval_days} onChange={e => set("recurring_interval_days", e.target.value)} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF8A65]/40">
                {RECURRING_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 pt-3 border-t border-slate-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-slate-200 text-slate-600 font-semibold text-sm hover:bg-slate-50 transition-colors">Cancel</button>
          <button data-testid="save-reminder-btn" onClick={handleSubmit} disabled={loading} className="flex-1 py-3 rounded-full bg-[#FF8A65] text-white font-semibold text-sm hover:bg-[#FF7043] transition-colors disabled:opacity-60">
            {loading ? "Saving..." : "Add Reminder"}
          </button>
        </div>
      </div>
    </div>
  );
}
